import { ChevronsUpDown } from "lucide-react";
import { FieldTitle, useInput, useTranslate } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { FormError, FormField, FormLabel } from "@/components/admin/form";
import { SelectInput } from "@/components/admin/select-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { CONTACT_TYPES, type ContactType } from "../types";
import { ContactLinkInput } from "./ContactLinkInput";
import { cleanupContactDependentFields } from "./contactModel";
import {
  AUTO_SET_CONTACT_TYPES,
  CONTACT_TYPE_LABEL_KEYS,
  typesRequireExtra,
} from "./contactTypes";
import { SupplierCategoryInput } from "./SupplierCategoryInput";
import { markRequired, useContactMandatory } from "./useContactMandatory";

/** Fields that only make sense while a given type is selected (cleared when it is unchecked). */
const TYPE_DEPENDENT_FIELDS = [
  "colega_kind",
  "colega_affiliation",
  "colega_agreement_signed",
  "supplier_category_id",
  "guarantor_of_contact_id",
] as const;

const COLEGA_KINDS = ["independiente", "inmobiliaria"] as const;
const COLEGA_AFFILIATIONS = ["ampi", "otra", "ninguna"] as const;
const AFFILIATION_REQUIRED_MESSAGE = "resources.contacts.affiliation_required";

/**
 * Almikia Epic EC2a: the mandatory multi-select of contact types plus the extra fields each
 * type asks for. Cliente and Arrendatario are set by the system: shown (checked, disabled) only
 * when already present, never offered for manual selection.
 */
export const ContactTypeInputs = () => {
  const types = (useWatch({ name: "contact_types" }) ?? []) as ContactType[];
  return (
    <div className="flex flex-col gap-4">
      <ContactTypesSelect />
      {typesRequireExtra(types, "colega") && <ColegaInputs />}
      {typesRequireExtra(types, "supplier") && <SupplierInputs />}
      {typesRequireExtra(types, "guarantor") && <GuarantorInputs />}
    </div>
  );
};

const ContactTypesSelect = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const { validate } = useContactMandatory();
  const { id, field, fieldState, isRequired } = useInput({
    source: "contact_types",
    validate: validate("contact_types"),
  });
  const selected = (field.value ?? []) as ContactType[];
  const label = translate("resources.contacts.fields.contact_types");

  const toggle = (type: ContactType, checked: boolean) => {
    const next = checked
      ? [...selected, type]
      : selected.filter((item) => item !== type);
    // Unchecking a type clears the values that only that type asked for.
    const current = Object.fromEntries(
      TYPE_DEPENDENT_FIELDS.map((name) => [name, getValues(name)]),
    );
    const cleaned = cleanupContactDependentFields({
      ...current,
      contact_types: next,
    }) as Record<string, unknown>;
    for (const name of TYPE_DEPENDENT_FIELDS) {
      if (cleaned[name] !== current[name]) {
        setValue(name, cleaned[name], { shouldDirty: true });
      }
    }
    field.onChange(next);
  };

  return (
    <FormField id={id} name={field.name}>
      <FormLabel htmlFor={undefined}>
        <FieldTitle
          label="resources.contacts.fields.contact_types"
          source="contact_types"
          resource="contacts"
          isRequired={isRequired}
        />
      </FormLabel>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-label={label}
            aria-invalid={fieldState.invalid || undefined}
            className="h-11 md:h-9 justify-between font-normal"
          >
            <span className="truncate">
              {selected.length > 0
                ? translate("resources.contacts.types_selected", {
                    smart_count: selected.length,
                  })
                : translate("resources.contacts.types_placeholder")}
            </span>
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) max-h-80 overflow-y-auto">
          {CONTACT_TYPES.map((type) => {
            const isAutoSet = AUTO_SET_CONTACT_TYPES.includes(type);
            const isChecked = selected.includes(type);
            if (isAutoSet && !isChecked) return null;
            return (
              <DropdownMenuCheckboxItem
                key={type}
                checked={isChecked}
                disabled={isAutoSet}
                className="min-h-11 md:min-h-8 text-base md:text-sm"
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(checked) => toggle(type, checked === true)}
              >
                {translate(CONTACT_TYPE_LABEL_KEYS[type])}
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((type) => (
            <Badge key={type} variant="secondary">
              {translate(CONTACT_TYPE_LABEL_KEYS[type])}
            </Badge>
          ))}
        </div>
      )}
      <FormError />
    </FormField>
  );
};

const ColegaInputs = () => {
  const translate = useTranslate();
  const { validate } = useContactMandatory();
  const toChoices = (prefix: string, values: readonly string[]) =>
    values.map((value) => ({
      id: value,
      name: translate(`resources.contacts.${prefix}.${value}`),
    }));
  return (
    <>
      <SelectInput
        source="colega_kind"
        showError
        label="resources.contacts.fields.colega_kind"
        choices={toChoices("colega_kinds", COLEGA_KINDS)}
        translateChoice={false}
        helperText={false}
        validate={validate("colega_kind")}
      />
      <SelectInput
        source="colega_affiliation"
        showError
        label="resources.contacts.fields.colega_affiliation"
        choices={toChoices("colega_affiliations", COLEGA_AFFILIATIONS)}
        translateChoice={false}
        helperText={false}
        validate={markRequired((value: unknown) =>
          value == null || value === ""
            ? AFFILIATION_REQUIRED_MESSAGE
            : undefined,
        )}
      />
      <SelectInput
        source="colega_agreement_signed"
        showError
        label="resources.contacts.fields.colega_agreement_signed"
        choices={[
          { id: "yes", name: translate("resources.contacts.agreement.yes") },
          { id: "no", name: translate("resources.contacts.agreement.no") },
        ]}
        translateChoice={false}
        helperText={false}
        format={(value: boolean | null | undefined) =>
          value == null ? "" : value ? "yes" : "no"
        }
        parse={(value: string) => (value === "" ? null : value === "yes")}
        validate={validate("colega_agreement_signed")}
      />
    </>
  );
};

const SupplierInputs = () => {
  const { validate } = useContactMandatory();
  return <SupplierCategoryInput validate={validate("supplier_category_id")} />;
};

const GuarantorInputs = () => {
  const { validate } = useContactMandatory();
  return (
    <ContactLinkInput
      source="guarantor_of_contact_id"
      label="resources.contacts.fields.guarantor_of_contact_id"
      validate={validate("guarantor_of_contact_id")}
    />
  );
};
